import {sanitizeDiacritics, toGreeklish} from "greek-utils";
import DIACRITICS from "greek-utils/lib/mappings/diacritics-map";
import GREEKLISH from "greek-utils/lib/mappings/greek-to-greeklish-map";

// greek-utils' own tables applied in one pass each, instead of its one regex
// per table row: same output, ~5x faster, which matters when the add-food
// modal indexes every food on open. The `(θη|Θη)` row can't fire here — η is
// already ι by then.
const DIACRITIC_FREE = {};
for (const {find, replace} of DIACRITICS) {
    for (const char of find) if (!(char in DIACRITIC_FREE)) DIACRITIC_FREE[char] = replace;
}
const DIACRITICS_RE = new RegExp(`[${Object.keys(DIACRITIC_FREE).join('')}]`, 'g');

const LATIN = {};
for (const {find, replace} of GREEKLISH) {
    if (!find.startsWith('(') && !(find in LATIN)) LATIN[find] = replace;
}
const GREEK_RE = new RegExp(
    Object.keys(LATIN).sort((a, b) => b.length - a.length).map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'),
    'g'
);

const toPlainSearch = field => field
    .replace(DIACRITICS_RE, char => DIACRITIC_FREE[char])
    .toLowerCase()
    .replaceAll('η', 'ι')
    .replaceAll('ω', 'ο')
    .replace(GREEK_RE, greek => LATIN[greek]);

// Greeklish types η, χ, φ as h, ch, ph where the transliteration gives i, x, f.
// th stays: it is θ as often as τη, which `thReadings` covers on the query side.
const foldGreeklishH = s => s
    .replaceAll('ch', 'x')
    .replaceAll('ph', 'f')
    .replaceAll('th', '\0')
    .replaceAll('h', 'i')
    .replaceAll('\0', 'th');

const thReadings = word => word.includes('th') ? [word, word.replaceAll('th', 'ti')] : [word];

// Phonetic collapse on top of the transliteration, applied to BOTH the food
// names and the query, so every greeklish typing convention lands on the same
// canon: ryzi/rizi → rizi, xalbas/xalvas/chalvas → xalbas, poutigka/poutiga
// → putiga, mpara/bara → bara, erwt/erot → erot, xrhstos → xristos. One-letter
// maps run first so the canon doesn't depend on spelling: royal and roial both → rial.
const phoneticCollapse = (s) => foldGreeklishH(s
    .replaceAll('w', 'o')
    .replaceAll('v', 'b')
    .replaceAll('y', 'i')
    .replaceAll('gk', 'g')
    .replaceAll('gg', 'g')
    .replaceAll('mp', 'b')
    .replaceAll('nt', 'd')
    .replaceAll('ai', 'e')
    .replaceAll('ei', 'i')
    .replaceAll('oi', 'i')
    .replaceAll('ou', 'u'));

// Cross-language pairs the transliteration can never bridge (english word vs
// greek word, greek phonetic spelling of a foreign brand). Keys and values
// are in the collapsed canon; extend as cases show up.
export const SEARCH_ALIASES = {
    pudding: ['putiga'],
    putiga: ['pudding'],
    sueps: ['sxoeppes'],
};

export const toNameSearch = field => phoneticCollapse(toPlainSearch(field));

export const toFoodSearchKeys = name => {
    const searchPlain = toPlainSearch(name);
    return {search: phoneticCollapse(searchPlain), searchPlain};
};

/**
 * Builds a predicate over foods carrying `toFoodSearchKeys`. Every query word
 * must appear somewhere in the food, in any order — «arla protein» matches
 * «...Protein ... · ARLA». A word matches the plain transliteration or the
 * phonetic canon (or an alias). The plain side keeps half-typed words working:
 * the collapse turns «γιαο» into giao but γιαούρτι into giaurti.
 */
export const foodSearchMatcher = query => {
    const words = toPlainSearch(query).split(' ').filter(Boolean).map(plain => {
        const phonetic = phoneticCollapse(plain);
        const phonetics = [...thReadings(phonetic), ...(SEARCH_ALIASES[phonetic] || [])];
        // A trailing c is a ch still being typed: «zac» → zaxari.
        if (phonetic.endsWith('c')) phonetics.push(phonetic.slice(0, -1) + 'x');
        // Greeklish folded against the plain side too: the phonetic canon runs its
        // vowel pairs before the h fold, so «zwh» only meets Ζωή as plain «zoi».
        const plains = [plain, ...thReadings(foldGreeklishH(plain.replaceAll('w', 'o')))];
        return {plains, phonetics};
    });
    return food => words.every(w =>
        w.plains.some(p => food.searchPlain.includes(p)) || w.phonetics.some(p => food.search.includes(p))
    );
};

/**
 * Normalizes any Greek/greeklish/mixed string into a comparable Latin form.
 * Handles diacritics, case, vowel folding (η→ι, ω→ο, greeklish w→o), and y→i unification.
 */
export const toSearchable = (str) =>
    toGreeklish(
        sanitizeDiacritics(str)
            .toLowerCase()
            .replaceAll('η', 'ι')
            .replaceAll('ω', 'ο')
    ).toLowerCase().replaceAll('y', 'i').replaceAll('w', 'o');

// Every query word must appear somewhere in `text`, in any order — «Παπαδόπουλος
// Γιώργος» finds «Γιώργος Παπαδόπουλος», and stray spaces on either side don't matter.
// Words match as typed or greeklish-folded; the as-typed side keeps half-typed
// Latin words («c» for Chloride) matching before the fold can see their h.
export const matchesSearch = (text, query) => {
    const words = toSearchable(query).split(' ').filter(Boolean);
    if (!words.length) return true;
    const plain = toSearchable(text);
    const folded = foldGreeklishH(plain);
    return words.every(word => plain.includes(word)
        || thReadings(foldGreeklishH(word)).some(reading => folded.includes(reading)));
};